/**
 * Lightweight app state (FOUNDATION — BUILD-PLAN src/lib/store.ts).
 *
 * Zero-dependency store built on useSyncExternalStore. Foundation ships the
 * slices the shell needs (session / ui / presence / ws-status); verticals may
 * create their OWN stores with `createStore` in their feature dir — they do
 * not add fields to the foundation slices.
 */
import { useSyncExternalStore } from "react";
import type { ApiComponents } from "@humble/shared";

export type UserRef = ApiComponents["schemas"]["UserRef"];

// ---------------------------------------------------------------------------
// generic store
// ---------------------------------------------------------------------------
export interface Store<T> {
  get(): T;
  /** Shallow-merge a patch (or updater returning one). Notifies subscribers. */
  set(patch: Partial<T> | ((state: T) => Partial<T>)): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(patch) {
      const next = typeof patch === "function" ? patch(state) : patch;
      state = { ...state, ...next };
      for (const l of listeners) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** React hook — select a slice; re-renders only when the selected value changes. */
export function useStore<T extends object, S>(store: Store<T>, selector: (state: T) => S): S {
  return useSyncExternalStore(
    store.subscribe,
    () => selector(store.get()),
    () => selector(store.get()),
  );
}

// ---------------------------------------------------------------------------
// session slice — access token lives in MEMORY ONLY (never storage); the
// refresh token is an httpOnly cookie the JS never sees (openapi TokenPair).
// ---------------------------------------------------------------------------
export type SessionStatus = "unknown" | "anon" | "authed";

export interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  user: UserRef | null;
  /** SM-12 lifecycle string from the last TokenPair (routing decisions only). */
  lifecycle: string | null;
}

export const sessionStore = createStore<SessionState>({
  status: "unknown",
  accessToken: null,
  user: null,
  lifecycle: null,
});

export function setSession(token: string, user: UserRef | null, lifecycle: string | null): void {
  sessionStore.set({ status: "authed", accessToken: token, user, lifecycle });
}

export function clearSession(): void {
  sessionStore.set({ status: "anon", accessToken: null, user: null, lifecycle: null });
}

// ---------------------------------------------------------------------------
// ui slice
// ---------------------------------------------------------------------------
export type AnnouncerVolume = "off" | "text" | "full";
export type ThemeChoice = "system" | "light" | "dark";

const VOLUME_KEY = "humble.announcer_volume";
const THEME_KEY = "humble.theme";

function readLocal<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return allowed.includes(v as T) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export interface UiState {
  online: boolean;
  /** PRD §10 announcer volume setting: off / text-only / full. */
  announcerVolume: AnnouncerVolume;
  theme: ThemeChoice;
  /** beforeinstallprompt captured event — presence drives the install CTA. */
  installPrompt: BeforeInstallPromptEvent | null;
}

/** Minimal typing for the (non-standard) install prompt event. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const uiStore = createStore<UiState>({
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  announcerVolume: readLocal(VOLUME_KEY, ["off", "text", "full"] as const, "full"),
  // Dark-first brand: default to the signature "After-Dark Arcade" dark theme
  // even on light-preference devices (there is no user toggle yet; light stays
  // reachable via an explicit stored "light"/"system" choice). DESIGN-BRIEF v3.
  theme: readLocal(THEME_KEY, ["system", "light", "dark"] as const, "dark"),
  installPrompt: null,
});

export function setAnnouncerVolume(v: AnnouncerVolume): void {
  uiStore.set({ announcerVolume: v });
  try {
    localStorage.setItem(VOLUME_KEY, v);
  } catch {
    /* private mode — non-fatal */
  }
}

export function setTheme(theme: ThemeChoice): void {
  uiStore.set({ theme });
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* non-fatal */
  }
  applyTheme(theme);
}

export function applyTheme(theme: ThemeChoice): void {
  const el = document.documentElement;
  if (theme === "system") delete el.dataset.theme;
  else el.dataset.theme = theme;
}

// ---------------------------------------------------------------------------
// presence slice — coarse buckets pushed over WS (#51); client renders only.
// ---------------------------------------------------------------------------
export type PresenceBucket = "online" | "today" | "this_week" | "none";

export interface PresenceState {
  byUser: Readonly<Record<string, PresenceBucket>>;
}

export const presenceStore = createStore<PresenceState>({ byUser: {} });

export function setPresence(userId: string, bucket: PresenceBucket): void {
  presenceStore.set((s) => ({ byUser: { ...s.byUser, [userId]: bucket } }));
}

// ---------------------------------------------------------------------------
// ws status slice (written by lib/ws.ts)
// ---------------------------------------------------------------------------
export type WsStatus = "idle" | "connecting" | "open" | "closed";

export const wsStore = createStore<{ status: WsStatus }>({ status: "idle" });
